import axios, { AxiosInstance, AxiosError } from 'axios';
import { authService } from './authService';
import { apiConfig, apiEndpoints } from '../config/apiConfig';
import { Agent, AgentQueryRequest, AgentQueryResponse, ChatSession } from '../types/agent.types';

/**
 * Agent Service
 * Handles all agent-related API calls
 */
class AgentService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: apiConfig.baseUrl,
      timeout: apiConfig.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // ✅ Skip auth when using local mock API
        const baseUrl = (config.baseURL || '').toString();
        if (baseUrl.includes('localhost:3001')) {
          return config;
        }

        try {
          const token = await authService.getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Failed to get access token:', error);
          // Do not force login here; allow the request to fail and UI can handle it
        }
        return config;
      },
      (error) => Promise.reject(error)
    );


    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // ✅ Don't force a login loop from API calls.
        // Let the UI decide what to do when unauthorized.
        return Promise.reject(error);
      }
    );

  }

  /**
   * Get all available agents
   */
  async getAgents(): Promise<Agent[]> {
    try {
      const response = await this.axiosInstance.get<Agent[]>(apiEndpoints.agents.list);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      throw error;
    }
  }

  /**
   * Get a specific agent by ID
   */
  async getAgent(agentId: string): Promise<Agent> {
    try {
      const response = await this.axiosInstance.get<Agent>(apiEndpoints.agents.get(agentId));
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch agent ${agentId}:`, error);
      throw error;
    }
  }

  /**
   * Query an agent. Accepts an optional AbortSignal so callers can cancel
   * in-flight requests (PR 9 — stop button during streaming + JSON fallback).
   */
  async queryAgent(request: AgentQueryRequest, signal?: AbortSignal): Promise<AgentQueryResponse> {
    try {
      const response = await this.axiosInstance.post<AgentQueryResponse>(
        apiEndpoints.agents.query(request.agentId),
        {
          query: request.query,
          sessionId: request.sessionId,
          context: request.context,
        },
        { signal },
      );
      return response.data;
    } catch (error) {
      console.error('Failed to query agent:', error);
      throw error;
    }
  }

  /**
   * Query an agent with token streaming (PR 6).
   *
   * Calls the SSE variant of the query endpoint and invokes the caller's
   * handlers as events arrive:
   *   - onToken(delta)     : each incremental piece of the AI prose
   *   - onComplete(payload): full AgentQueryResponse — including citations,
   *                          chartData, trace, expandableTable, suggestions
   *   - onError(message)   : SSE error event or network failure
   *
   * Throws if the response is non-200 (e.g. 401 / 403 / 402 budget). The
   * caller should catch and either surface the error or fall back to
   * the non-streaming queryAgent() path.
   */
  async queryAgentStream(
    request: AgentQueryRequest,
    handlers: {
      onToken?: (delta: string) => void;
      onComplete?: (payload: AgentQueryResponse) => void;
      onError?: (message: string) => void;
      signal?: AbortSignal;
    },
  ): Promise<void> {
    // Build auth headers manually — fetch() doesn't use axios interceptors,
    // and we need its readable stream support.
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const baseUrl = (apiConfig.baseUrl || '').toString();
    if (!baseUrl.includes('localhost:3001')) {
      try {
        const token = await authService.getAccessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      } catch (e) {
        // Same fall-through behaviour as queryAgent — let the request fail.
        console.error('Failed to get access token:', e);
      }
    }

    const url = (baseUrl ? baseUrl.replace(/\/$/, '') : '') + apiEndpoints.agents.queryStream(request.agentId);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: request.query,
        sessionId: request.sessionId,
        context: request.context,
      }),
      signal: handlers.signal,
    });

    if (!response.ok || !response.body) {
      // Try to surface server message before throwing.
      let detail = `HTTP ${response.status}`;
      try {
        const text = await response.text();
        if (text) detail = text;
      } catch (_) { /* ignore */ }
      throw new Error(`Stream request failed: ${detail}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Helper: parse and dispatch one SSE event (text between blank lines).
    const dispatch = (eventText: string) => {
      const dataLine = eventText.split('\n').find((l) => l.startsWith('data:'));
      if (!dataLine) return;
      const payload = dataLine.slice(5).trim();
      if (!payload) return;
      try {
        const evt = JSON.parse(payload);
        if (evt.type === 'token' && typeof evt.delta === 'string') {
          handlers.onToken?.(evt.delta);
        } else if (evt.type === 'complete') {
          // Strip the envelope type before forwarding; the rest matches
          // AgentQueryResponse exactly.
          const { type, ...rest } = evt;
          handlers.onComplete?.(rest as AgentQueryResponse);
        } else if (evt.type === 'error') {
          handlers.onError?.(evt.message || 'Stream error');
        }
      } catch (_) {
        // Malformed event — ignore. The stream may include keepalives.
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE events are delimited by blank lines (\n\n).
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';
      for (const part of parts) {
        if (part.trim()) dispatch(part);
      }
    }
    // Flush any final event that wasn't terminated by \n\n.
    if (buffer.trim()) dispatch(buffer);
  }

  /**
   * Get conversation history for a session
   */
  async getConversationHistory(agentId: string, sessionId: string): Promise<ChatSession> {
    try {
      const response = await this.axiosInstance.get<ChatSession>(
        apiEndpoints.agents.history(agentId, sessionId)
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch conversation history:', error);
      throw error;
    }
  }

  /**
   * General chat (no agent required) — for file analysis and general questions
   */
  async chatGeneral(query: string, fileContext?: string, sessionId?: string): Promise<AgentQueryResponse> {
    try {
      const response = await this.axiosInstance.post<AgentQueryResponse>(
        '/api/chat',
        { query, fileContext, sessionId }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to chat:', error);
      throw error;
    }
  }

  /**
   * Submit feedback for a message
   */
  async submitFeedback(
    agentId: string,
    sessionId: string,
    messageId: string,
    feedback: 'positive' | 'negative',
    comment?: string
  ): Promise<void> {
    try {
      await this.axiosInstance.post(`/agents/${agentId}/feedback`, {
        sessionId,
        messageId,
        feedback,
        comment,
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error;
    }
  }
}

// Export singleton instance
// Export singleton instance
export const agentService = new AgentService();


